import { useEffect, useRef, useState } from "react";
import { Settings, Users, Building2, TrendingUp, Bot, CheckCheck } from "lucide-react";
import { useInView } from "@/hooks/useInView";
import { cn } from "@/lib/utils";

const audienceSegments = [
  { icon: Settings, lines: ["Pequenas e Médias", "Empresas"] },
  { icon: Users, lines: ["Equipes de Vendas", "e Atendimento"] },
  { icon: Building2, lines: ["Clínicas, Agências", "e Serviços"] },
  { icon: TrendingUp, lines: ["Ecommerces e", "Lojas virtuais"] },
] as const;

type DemoMessage = {
  id: string;
  from: "customer" | "bot";
  text: string;
  time: string;
  pauseBefore?: number;
};

const demoMessages: DemoMessage[] = [
  {
    id: "m1",
    from: "customer",
    text: "Oi! Vi o Instagram de vocês. Quanto fica a limpeza de pele?",
    time: "15:42",
    pauseBefore: 800,
  },
  {
    id: "m2",
    from: "bot",
    text: "Olá! A limpeza de pele sai por R$ 180. Temos vaga amanhã e sexta.",
    time: "15:42",
    pauseBefore: 900,
  },
  {
    id: "m3",
    from: "customer",
    text: "Sexta à tarde tem horário?",
    time: "15:43",
    pauseBefore: 1400,
  },
  {
    id: "m4",
    from: "bot",
    text: "Tenho sexta às 15h ou 17h. Qual prefere?",
    time: "15:43",
    pauseBefore: 800,
  },
  {
    id: "m5",
    from: "customer",
    text: "15h, por favor",
    time: "15:44",
    pauseBefore: 1200,
  },
  {
    id: "m6",
    from: "bot",
    text: "Agendado sexta às 15h! Te mando a confirmação agora.",
    time: "15:44",
    pauseBefore: 700,
  },
];

const TYPING_MS = 850;
const LOOP_PAUSE_MS = 4000;
const STEP_GAP_MS = 300;

function TypingIndicator() {
  return (
    <div className="relative pl-6 animate-chat-bubble-in">
      <div className="absolute left-0 top-0 z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
        <Bot className="h-2.5 w-2.5" aria-hidden />
      </div>
      <div className="rounded-lg rounded-tl-sm bg-muted/80 px-2.5 py-1.5 flex items-center gap-0.5">
        {[0, 150, 300].map((delay) => (
          <span
            key={delay}
            className="h-1 w-1 rounded-full bg-muted-foreground/40 animate-bounce"
            style={{ animationDelay: `${delay}ms`, animationDuration: "0.9s" }}
          />
        ))}
      </div>
    </div>
  );
}

function DemoChat({
  visibleMessages,
  showTyping,
  cycle,
  maxVisible = 6,
}: {
  visibleMessages: DemoMessage[];
  showTyping: boolean;
  cycle: number;
  maxVisible?: number;
}) {
  const lastId = visibleMessages.at(-1)?.id;
  const displayMessages =
    visibleMessages.length > maxVisible
      ? visibleMessages.slice(-maxVisible)
      : visibleMessages;

  return (
    <div
      className="flex flex-col gap-1 pointer-events-none"
      aria-live="polite"
      aria-label="Demonstração de atendimento automático"
    >
      {displayMessages.map((msg) => {
        const isBot = msg.from === "bot";
        const isNew = msg.id === lastId;

        if (isBot) {
          return (
            <div
              key={`${cycle}-${msg.id}`}
              className={cn("relative pl-6", isNew && "animate-chat-bubble-in")}
            >
              <div className="absolute left-0 top-0 z-10 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                <Bot className="h-2.5 w-2.5" aria-hidden />
              </div>
              <div className="rounded-lg rounded-tl-sm bg-muted/90 px-2 py-1.5 text-[10px] leading-snug text-foreground">
                <p>{msg.text}</p>
                <div className="mt-0.5 flex justify-end text-[8px] text-muted-foreground">
                  <span>{msg.time}</span>
                </div>
              </div>
            </div>
          );
        }

        return (
          <div
            key={`${cycle}-${msg.id}`}
            className={cn("flex justify-end", isNew && "animate-chat-bubble-in")}
          >
            <div className="max-w-[92%] rounded-lg rounded-tr-sm bg-[#DCF8C6] text-[#111] px-2 py-1.5 text-[10px] leading-snug">
              <p>{msg.text}</p>
              <div className="mt-0.5 flex items-center justify-end gap-0.5 text-[8px] text-[#667781]">
                <span>{msg.time}</span>
                <CheckCheck className="h-2 w-2 text-sky-500/70" aria-hidden />
              </div>
            </div>
          </div>
        );
      })}

      {showTyping && <TypingIndicator key={`typing-${cycle}`} />}
    </div>
  );
}

function useDemoChatLoop(isActive: boolean) {
  const [visibleMessages, setVisibleMessages] = useState<DemoMessage[]>([]);
  const [showTyping, setShowTyping] = useState(false);
  const [cycle, setCycle] = useState(0);
  const timersRef = useRef<number[]>([]);

  useEffect(() => {
    const clearTimers = () => {
      timersRef.current.forEach(clearTimeout);
      timersRef.current = [];
    };

    const schedule = (fn: () => void, ms: number) => {
      const id = window.setTimeout(fn, ms);
      timersRef.current.push(id);
    };

    if (!isActive) {
      clearTimers();
      setVisibleMessages([]);
      setShowTyping(false);
      return clearTimers;
    }

    let step = 0;
    let cancelled = false;

    const runStep = () => {
      if (cancelled) return;

      if (step >= demoMessages.length) {
        schedule(() => {
          if (cancelled) return;
          setVisibleMessages([]);
          setShowTyping(false);
          setCycle((c) => c + 1);
          step = 0;
          schedule(runStep, 600);
        }, LOOP_PAUSE_MS);
        return;
      }

      const msg = demoMessages[step];
      const pause = msg.pauseBefore ?? 1000;

      if (msg.from === "bot") {
        schedule(() => {
          if (cancelled) return;
          setShowTyping(true);
          schedule(() => {
            if (cancelled) return;
            setShowTyping(false);
            setVisibleMessages((prev) => [...prev, msg]);
            step += 1;
            schedule(runStep, STEP_GAP_MS);
          }, TYPING_MS);
        }, pause);
      } else {
        schedule(() => {
          if (cancelled) return;
          setShowTyping(false);
          setVisibleMessages((prev) => [...prev, msg]);
          step += 1;
          schedule(runStep, STEP_GAP_MS);
        }, pause);
      }
    };

    setVisibleMessages([]);
    setShowTyping(false);
    schedule(runStep, 500);

    return () => {
      cancelled = true;
      clearTimers();
    };
  }, [isActive]);

  return { visibleMessages, showTyping, cycle };
}

export const WhoIsItFor = () => {
  const { ref, isInView } = useInView({ threshold: 0.25 });
  const [isChatActive, setIsChatActive] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsChatActive(entry.isIntersecting),
      { threshold: 0.3 },
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  const { visibleMessages, showTyping, cycle } = useDemoChatLoop(isChatActive);

  return (
    <section
      ref={ref}
      className="relative py-16 md:py-20 lg:py-24 bg-background overflow-hidden"
    >
      <div className="container mx-auto px-4 max-w-6xl">
        <div className="grid grid-cols-1 lg:grid-cols-[0.92fr_1.08fr] gap-10 lg:gap-12 xl:gap-16 items-stretch">
          <div className="order-2 lg:order-1 flex flex-col">
            <span
              className={cn(
                "mb-4 inline-flex items-center self-start rounded-full border border-primary/30 bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary shadow-sm ring-1 ring-primary/15 transition-all duration-700",
                isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
              )}
            >
              Para quem é
            </span>
            <h2
              className={cn(
                "text-3xl md:text-4xl font-bold text-foreground leading-tight mb-4 text-balance transition-all duration-700 delay-100",
                isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
              )}
            >
              Se você vende pelo{" "}
              <span className="text-primary">WhatsApp</span> ou{" "}
              <span className="text-primary">Instagram</span>, isso é para o seu negócio
            </h2>
            <p
              className={cn(
                "text-base md:text-lg text-muted-foreground leading-relaxed mb-8 transition-all duration-700 delay-150",
                isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
              )}
            >
              Para quem vende pelo WhatsApp ou Instagram e não quer perder lead por demora na resposta,
              sem precisar dobrar a equipe.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mb-8">
              {audienceSegments.map(({ icon: Icon, lines }, index) => (
                <div
                  key={lines.join("-")}
                  className={cn(
                    "flex items-start gap-3 rounded-2xl border border-border bg-card p-4 shadow-[0_2px_12px_-4px_hsl(270_30%_20%_/_0.12)]",
                    "hover:border-primary/35 hover:shadow-[0_4px_16px_-4px_hsl(var(--primary)_/_0.18)] transition-all duration-300",
                    isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6",
                  )}
                  style={{ transitionDelay: `${200 + index * 80}ms` }}
                >
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-primary/15 text-primary ring-1 ring-primary/20">
                    <Icon className="h-5 w-5" aria-hidden />
                  </div>
                  <h3 className="text-sm md:text-base font-bold text-foreground leading-snug pt-0.5">
                    {lines[0]}
                    <span className="block">{lines[1]}</span>
                  </h3>
                </div>
              ))}
            </div>

            <p
              className={cn(
                "text-lg md:text-xl font-medium text-foreground leading-relaxed transition-all duration-700 delay-300",
                isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4",
              )}
            >
              PME, loja online, clínica ou equipe comercial: se o cliente te chama no celular, a
              FabChat{" "}
              <span className="text-primary font-semibold">
                responde, qualifica e não deixa lead esfriar.
              </span>
            </p>
          </div>

          <div
            className={cn(
              "order-1 lg:order-2 relative w-full max-w-[340px] sm:max-w-md mx-auto lg:max-w-none lg:mx-0 lg:h-full lg:min-h-0 transition-all duration-700 delay-200",
              isInView ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8",
            )}
          >
            <div className="relative h-[min(400px,88vw)] sm:h-[min(440px,75vw)] lg:h-full lg:min-h-[360px]">
              <div className="absolute inset-0 lg:left-[32%] lg:right-0 rounded-[1.75rem] sm:rounded-[2rem] md:rounded-[2.5rem] overflow-hidden shadow-[0_24px_48px_-12px_hsl(270_50%_40%_/_0.25)] ring-1 ring-border/40">
                <img
                  src="/landing/who-is-it-for.png"
                  alt="Empresária atendendo clientes pelo celular com FabChat"
                  className="h-full w-full object-cover object-[68%_18%] lg:object-[72%_18%]"
                  loading="lazy"
                  decoding="async"
                />
                <div
                  className="absolute inset-0 bg-gradient-to-r from-black/25 via-transparent to-transparent pointer-events-none lg:from-black/15"
                  aria-hidden
                />
              </div>

              <div
                className={cn(
                  "absolute z-10 w-[min(176px,46%)] sm:w-[188px]",
                  "left-0 top-2.5 sm:top-3",
                  "lg:-left-6 lg:top-[12%] xl:-left-8",
                  "lg:w-[200px]",
                )}
              >
                <div className="rounded-lg bg-card/90 backdrop-blur-sm shadow-lg p-2">
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-primary text-primary-foreground">
                      <Bot className="h-2.5 w-2.5" aria-hidden />
                    </div>
                    <p className="text-[10px] font-medium text-foreground leading-tight">
                      IA
                      <span className="text-emerald-600 font-normal"> online</span>
                    </p>
                  </div>
                  <DemoChat
                    visibleMessages={visibleMessages}
                    showTyping={showTyping}
                    cycle={cycle}
                    maxVisible={3}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
